import { useQuery } from '@tanstack/react-query';
import { GitBranch, GitCommitHorizontal } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GIT_PROGRESS_COMMITS_MAX, type GitProgressBranch } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { Dialog } from '../../../components/ui/dialog';
import { displayDateTime } from '../../../lib/date';
import { errorMessage } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { progressCommitsApi, progressCommitsKeys } from '../progress-commits-api';

/** Một commit đã chọn — đủ để gửi `ReportGitProgressCommitsInput.commits`. */
export interface PickedCommit {
  readonly repositoryId: string;
  readonly branch: string;
  readonly sha: string;
}

export type ProgressCommitChoice =
  | { readonly skipped: true }
  | { readonly skipped: false; readonly commits: readonly PickedCommit[] };

/** Khoá của một commit trong một kho — `linked` dùng cùng dạng. */
export function commitKey(repositoryId: string, sha: string): string {
  return `${repositoryId}:${sha}`;
}

const branchKey = (branch: Pick<GitProgressBranch, 'repositoryId' | 'name'>): string =>
  `${branch.repositoryId}\u0000${branch.name}`;

/**
 * Hộp chọn **nhánh → nhiều commit** cho một thao tác tiến độ — APM-PCOM-c,
 * docs/plan-progress-commits.md §4.2.
 *
 * - **Dropdown nhánh ở trên, danh sách commit ở dưới** (người dùng chốt 2026-10-30).
 *   Nhánh đọc bản sao; nhánh mang mã việc đứng đầu và được **chọn sẵn**.
 * - Commit gọi máy chủ git (≤ 100 mới nhất), **mới nhất trước**; mặc định chỉ commit
 *   của chính mình (theo email), công tắc "Hiện commit của người khác" mở tất cả.
 * - Đổi nhánh **giữ** lựa chọn ở nhánh khác — một lần báo gồm commit nhiều nhánh được.
 * - "Không có commit" = bỏ qua (vẫn ghi lại); ✕ / Esc = huỷ **cả thao tác**.
 */
export function ProgressCommitDialog({
  projectId,
  taskId,
  heading,
  linked,
  pending = false,
  onSubmit,
  onCancel,
}: {
  readonly projectId: string;
  readonly taskId: string;
  /** "Tick: ‹mục›" / "Hoàn thành: ‹việc›". */
  readonly heading: string;
  /** Commit đã báo trước đó cho đúng việc / mục này — `commitKey`. */
  readonly linked: ReadonlySet<string>;
  readonly pending?: boolean;
  readonly onSubmit: (choice: ProgressCommitChoice) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<GitProgressBranch | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [picked, setPicked] = useState<ReadonlyMap<string, PickedCommit>>(new Map());

  const branches = useQuery({
    queryKey: progressCommitsKeys.branches(projectId, taskId),
    queryFn: () => progressCommitsApi.branches(projectId, taskId),
    staleTime: 30_000,
  });

  // Chọn sẵn nhánh đầu danh sách (nhánh mang mã việc đứng đầu) khi danh sách về.
  useEffect(() => {
    if (current === null && branches.data && branches.data.branches.length > 0) {
      setCurrent(branches.data.branches[0]!);
    }
  }, [branches.data, current]);

  const commits = useQuery({
    queryKey: progressCommitsKeys.commits(
      projectId,
      current?.repositoryId ?? '',
      current?.name ?? '',
      showAll,
    ),
    queryFn: () =>
      progressCommitsApi.commits(projectId, current!.repositoryId, current!.name, showAll),
    enabled: current !== null,
    staleTime: 30_000,
    retry: false,
  });

  const allBranches = useMemo(() => branches.data?.branches ?? [], [branches.data]);
  // Nhóm theo kho, giữ thứ tự server (nhánh mang mã việc đứng đầu).
  const repositoryGroups = useMemo(() => {
    const groups = new Map<string, GitProgressBranch[]>();
    for (const branch of allBranches) {
      groups.set(branch.repositoryPath, [...(groups.get(branch.repositoryPath) ?? []), branch]);
    }
    return [...groups.entries()];
  }, [allBranches]);
  const multiRepo = repositoryGroups.length > 1;

  const toggle = (sha: string): void => {
    if (!current) return;
    setPicked((before) => {
      const next = new Map(before);
      const key = commitKey(current.repositoryId, sha);
      if (next.has(key)) next.delete(key);
      else if (next.size < GIT_PROGRESS_COMMITS_MAX) {
        next.set(key, { repositoryId: current.repositoryId, branch: current.name, sha });
      }
      return next;
    });
  };
  const pickedOn = (branch: GitProgressBranch): number =>
    [...picked.values()].filter(
      (commit) => commit.repositoryId === branch.repositoryId && commit.branch === branch.name,
    ).length;
  /** `feature/x · nhánh của việc này · 2 đã chọn` — một dòng của dropdown. */
  const branchLabel = (branch: GitProgressBranch): string => {
    const count = pickedOn(branch);
    return [
      branch.name,
      branch.matchesTask ? t('progressCommits.dialog.matchesTask') : null,
      branch.isDefault ? t('progressCommits.dialog.defaultBranch') : null,
      count > 0 ? t('progressCommits.dialog.pickedOnBranch', { count }) : null,
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');
  };

  return (
    <Dialog label={t('progressCommits.dialog.title')} onClose={onCancel} size="lg" fixedHeight>
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold">{t('progressCommits.dialog.title')}</h2>
        <p className="text-sm font-medium">{heading}</p>
        <p className="text-xs text-muted-foreground">{t('progressCommits.dialog.help')}</p>
      </header>

      {/* Bước 1 — chọn nhánh (dropdown) */}
      <section
        aria-label={t('progressCommits.dialog.branchStep')}
        className="flex flex-col gap-1.5"
      >
        <label
          htmlFor="progress-commit-branch"
          className="flex items-center gap-1.5 text-xs font-medium"
        >
          <GitBranch aria-hidden className="size-3.5" />
          {t('progressCommits.dialog.branchStep')}
        </label>
        {branches.isPending ? (
          <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
        ) : branches.isError ? (
          <p className="text-xs text-rag-red">{errorMessage(branches.error, t)}</p>
        ) : allBranches.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('progressCommits.dialog.noBranch')}</p>
        ) : (
          <select
            id="progress-commit-branch"
            data-testid="progress-commit-branch"
            className="h-9 w-full rounded-[12px] border border-border-strong bg-background px-3 font-mono text-sm text-foreground"
            value={current ? branchKey(current) : ''}
            onChange={(event) =>
              setCurrent(allBranches.find((b) => branchKey(b) === event.target.value) ?? null)
            }
          >
            {repositoryGroups.map(([path, list]) =>
              multiRepo ? (
                <optgroup key={path} label={path}>
                  {list.map((branch) => (
                    <option key={branchKey(branch)} value={branchKey(branch)}>
                      {branchLabel(branch)}
                    </option>
                  ))}
                </optgroup>
              ) : (
                list.map((branch) => (
                  <option key={branchKey(branch)} value={branchKey(branch)}>
                    {branchLabel(branch)}
                  </option>
                ))
              ),
            )}
          </select>
        )}
        {current?.lastCommitAt ? (
          <p className="text-[11px] text-muted-foreground">
            {t('progressCommits.dialog.lastCommitAt', {
              at: displayDateTime(current.lastCommitAt),
            })}
          </p>
        ) : null}
      </section>

      {/* Bước 2 — commit */}
      <section
        aria-label={t('progressCommits.dialog.commitStep')}
        className="flex min-h-0 flex-1 flex-col gap-2 border-t border-border pt-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-xs font-medium">
            <GitCommitHorizontal aria-hidden className="size-3.5" />
            {t('progressCommits.dialog.commitStep')}
          </h3>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              className="ios-switch"
              checked={showAll}
              onChange={(event) => setShowAll(event.target.checked)}
            />
            {t('progressCommits.dialog.showOthers')}
          </label>
        </div>
        <ul
          className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto"
          data-testid="progress-commit-list"
        >
          {current === null ? (
            <li className="text-xs text-muted-foreground">
              {t('progressCommits.dialog.pickBranch')}
            </li>
          ) : commits.isPending ? (
            <li className="text-xs text-muted-foreground">
              {t('progressCommits.dialog.loadingCommits')}
            </li>
          ) : commits.isError ? (
            <li className="text-xs text-rag-red">{errorMessage(commits.error, t)}</li>
          ) : commits.data.commits.length === 0 ? (
            <li className="flex flex-col gap-1 text-xs text-muted-foreground">
              <span>
                {commits.data.myEmails === 0 || commits.data.hiddenOthers > 0
                  ? t('progressCommits.dialog.noneMine')
                  : t('progressCommits.dialog.noCommits')}
              </span>
              {commits.data.hiddenOthers > 0 ? (
                <span>{t('progressCommits.dialog.mapEmailHint')}</span>
              ) : null}
            </li>
          ) : (
            commits.data.commits.map((commit) => {
              const key = commitKey(current.repositoryId, commit.sha);
              const checked = picked.has(key);
              return (
                <li key={commit.sha}>
                  <label
                    className={cn(
                      'flex items-start gap-2 rounded-DEFAULT border px-2 py-1.5 text-sm',
                      checked ? 'border-primary bg-primary/5' : 'border-border',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="ios-check mt-0.5 shrink-0"
                      checked={checked}
                      onChange={() => toggle(commit.sha)}
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="break-words">{commit.title}</span>
                      <span className="flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
                        <span className="font-mono">{commit.shortSha}</span>
                        {commit.authorName ? <span>{commit.authorName}</span> : null}
                        <span>{displayDateTime(commit.committedAt)}</span>
                        {linked.has(key) ? (
                          <span className="rounded bg-muted px-1">
                            {t('progressCommits.dialog.alreadyLinked')}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })
          )}
        </ul>
      </section>

      <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={() => onSubmit({ skipped: true })}
        >
          {t('progressCommits.dialog.skip')}
        </Button>
        <Button
          type="button"
          disabled={pending || picked.size === 0}
          onClick={() => onSubmit({ skipped: false, commits: [...picked.values()] })}
        >
          {t('progressCommits.dialog.submit', { count: picked.size })}
        </Button>
      </footer>
    </Dialog>
  );
}
